import { useState, useEffect } from "react";
import { Box, Modal } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import theme from "../../assets/theme";
import QRStep from "./components/QRstep.jsx";
import OTPStep from "./components/OTPstep.jsx";

const loginData = window.tmtPortalLogin || {};
const loginUrl = `${window.location.origin}/wp-json/tmt-portal/v1/login`;

/**
 * Where to send someone once they are signed in.
 *
 * redirect_forced means the URL carried an explicit ?redirect_to that the
 * server validated as same-site, and it takes precedence over the role's
 * dashboard. Everything else falls back to the previous behaviour.
 *
 * It arrives as "1" or "" rather than a boolean: wp_localize_script() casts
 * every scalar to a string. Test it for truthiness, never with === true.
 */
const resolveDestination = (portalUrl) => {
	if (loginData.redirect_forced && loginData.redirect_url) {
		return loginData.redirect_url;
	}

	return portalUrl || loginData.redirect_url || "/";
};

const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";
const turnstileSiteKey = tmtPortalLogin.turnstile_site_key || "";

const Login = () => {
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [isLoggedIn] = useState(Boolean(loginData.is_login));
	const [captchaToken, setCaptchaToken] = useState("");
	const [isCaptchaVerifying, setIsCaptchaVerifying] = useState(true);
	const isTurnstileConfigured = Boolean(turnstileSiteKey.trim());
	const [redirectUrl, setRedirectUrl] = useState("");

	// MFA state
	const [step, setStep] = useState(localStorage.getItem("tmt_mfa_step") || "login"); // "login" | "qr" | "otp"
	const [mfaData, setMfaData] = useState(JSON.parse(localStorage.getItem("tmt_mfa_data")) || null); // { temp_token, qr_code, secret_string, is_new_setup }

	useEffect(() => {
		localStorage.setItem("tmt_mfa_step", step);
	}, [step]);

	useEffect(() => {
		if (!isTurnstileConfigured) {
			return;
		}

		window.onTurnstileVerifying = () => {
			setCaptchaToken("");
			setIsCaptchaVerifying(true);
		};

		window.onTurnstileSuccess = (token) => {
			setCaptchaToken(token || "");
			setIsCaptchaVerifying(false);
		};

		window.onTurnstileExpired = () => {
			setCaptchaToken("");
			setIsCaptchaVerifying(true);
		};

		window.onTurnstileError = () => {
			setCaptchaToken("");
			setIsCaptchaVerifying(true);
		};

		return () => {
			delete window.onTurnstileVerifying;
			delete window.onTurnstileSuccess;
			delete window.onTurnstileExpired;
			delete window.onTurnstileError;
		};
	}, [isTurnstileConfigured]);

	useEffect(() => {
		if (!isTurnstileConfigured) {
			return;
		}

		const existingScript = document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);

		if (existingScript) {
			return;
		}

		const script = document.createElement("script");
		script.src = TURNSTILE_SCRIPT_SRC;
		script.async = true;
		script.defer = true;
		document.head.appendChild(script);
	}, [isTurnstileConfigured]);

	const handleSubmit = async (event) => {
		event.preventDefault();

		if (isSubmitting) {
			return;
		}

		if (!isTurnstileConfigured) {
			setErrorMessage("Login is temporarily unavailable because captcha is not configured. Please contact support.");
			setIsSubmitting(false);
			return;
		}

		const formToken = event.currentTarget?.querySelector('input[name="cf-turnstile-response"]')?.value?.trim() || "";
		const token = formToken || captchaToken;

		if (!token) {
			setErrorMessage("Please complete the captcha challenge.");
			setIsSubmitting(false);
			return;
		}

		if (token !== captchaToken) {
			setCaptchaToken(token);
		}

		setIsSubmitting(true);
		setErrorMessage("");

		try {
			const response = await fetch(loginUrl, {
				method: "POST",
				headers: {
					"Content-Type": "application/json; charset=UTF-8",
					"X-WP-Nonce": loginData.nonce || "",
				},
				credentials: "same-origin",
				body: JSON.stringify({
					username,
					password,
					roles: loginData.roles || ["administrator"],
					redirect_url: loginData.redirect_url || "",
					captcha_token: token,
				}),
			});

			const payload = await response.json();
			console.log("payload", payload);

			if (!response.ok) {
				setCaptchaToken("");
				setIsCaptchaVerifying(true);
				if (window.turnstile) {
					window.turnstile.reset();
				}
				throw new Error(payload?.message || "Login failed.");
			}

			if (payload?.mfa_required) {
				// Same precedence after an MFA challenge as without one.
				setRedirectUrl(resolveDestination(payload?.user?.data?.portal_url));
				const dataObj = {
					temp_token: payload.temp_token,
					qr_code: payload.qr_code,
					secret_string: payload.secret_string,
					is_new_setup: payload.is_new_setup,
				};

				setMfaData(dataObj);
				localStorage.setItem("tmt_mfa_data", JSON.stringify(dataObj));
				setIsSubmitting(false);
				setStep(payload.is_new_setup ? "qr" : "otp");
				return;
			}

			if (payload?.active_role) {
				localStorage.setItem("active_role", payload.active_role);
			}

			/*
			 * A redirect_to on the URL beats the role-based portal_url. Without
			 * this the server's dashboard destination always won and a deep
			 * link into the portal was lost at the login step.
			 */
			window.location.assign(
				resolveDestination(
					payload?.user?.data?.portal_url || payload?.data?.portal_url
				)
			);
		} catch (error) {
			setCaptchaToken("");
			setIsCaptchaVerifying(true);
			if (window.turnstile) {
				window.turnstile.reset();
			}
			setErrorMessage(error.message || "Login failed.");
			setIsSubmitting(false);
		}
	};

	const resetToLogin = () => {
		localStorage.removeItem("tmt_mfa_step");
		localStorage.removeItem("tmt_mfa_data");
		setMfaData(null);
		setStep("login");
		setPassword("");
		setErrorMessage("");
		setCaptchaToken("");
		setIsCaptchaVerifying(true);
		if (window.turnstile) {
			window.turnstile.reset();
		}
	};

	const mfaStep = (step === "qr" || step === "otp") && mfaData && (
		<ThemeProvider theme={theme}>
			<Modal open onClose={() => {}} sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2, overflowY: "auto" }}>
				<Box sx={{ maxWidth: 750, width: "100%", outline: "none", my: "auto" }}>
					{step === "qr" ? (
						<QRStep
							qrCode={mfaData.qr_code}
							secretString={mfaData.secret_string}
							onNext={() => setStep("otp")}
							onBackToLogin={resetToLogin}
						/>
					) : (
						<OTPStep
							tempToken={mfaData.temp_token}
							onBack={mfaData.is_new_setup ? () => setStep("qr") : resetToLogin}
							onBackToLogin={resetToLogin}
							redirectUrl={redirectUrl || "/"}
						/>
					)}
				</Box>
			</Modal>
		</ThemeProvider>
	);

	return (
		<div
			style={{
				minHeight: "60vh",
				width: "100%",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				padding: "24px",
				backgroundColor: "#ffffff",
				fontFamily: "Inter, Arial, sans-serif",
			}}
		>
			{isLoggedIn ? (
				<p
					style={{
						margin: 0,
						color: "#111827",
						fontSize: "16px",
						fontWeight: 600,
					}}
				>
					{loginData.logged_in_message || "Already logged in."}
				</p>
			) : (
				<form
					onSubmit={handleSubmit}
					style={{
						width: "100%",
						maxWidth: "400px",
						display: "flex",
						flexDirection: "column",
						gap: "14px",
					}}
				>
					{mfaStep}

					<label style={{ display: "block", color: "#111827", fontSize: "16px" }}>
						<span style={{ display: "block", marginBottom: "8px" }}>Email Address or Username</span>
						<input
							type="text"
							value={username}
							onChange={(event) => setUsername(event.target.value)}
							required
							autoComplete="username"
							style={{
								width: "100%",
								height: "40px",
								padding: "8px 16px",
								border: "1px solid #d1d5db",
								borderRadius: "4px",
								backgroundColor: "#ffffff",
								color: "#111827",
								outlineOffset: "2px",
								boxSizing: "border-box",
							}}
						/>
					</label>

					<label style={{ display: "block", color: "#111827", fontSize: "16px" }}>
						<span style={{ display: "block", marginBottom: "8px" }}>Password</span>
						<div style={{ position: "relative" }}>
							<input
								type={showPassword ? "text" : "password"}
								value={password}
								onChange={(event) => setPassword(event.target.value)}
								required
								autoComplete="current-password"
								style={{
									width: "100%",
									height: "40px",
									padding: "8px 44px 8px 16px",
									border: "1px solid #d1d5db",
									borderRadius: "4px",
									backgroundColor: "#ffffff",
									color: "#111827",
									outlineOffset: "2px",
									boxSizing: "border-box",
								}}
							/>
							<button
								type="button"
								onClick={() => setShowPassword((value) => !value)}
								aria-label={showPassword ? "Hide password" : "Show password"}
								style={{
									position: "absolute",
									right: "12px",
									top: "50%",
									transform: "translateY(-50%)",
									border: 0,
									padding: 0,
									background: "transparent",
									color: "#4b5563",
									cursor: "pointer",
									display: "inline-flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
									<path
										d="M2 12C3.73 7.61 7.52 5 12 5C16.48 5 20.27 7.61 22 12C20.27 16.39 16.48 19 12 19C7.52 19 3.73 16.39 2 12Z"
										stroke="currentColor"
										strokeWidth="1.8"
									/>
									<circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
								</svg>
							</button>
						</div>
					</label>

					{!isTurnstileConfigured ? (
						<div>
							<div>Login is temporarily unavailable because captcha is not configured. Please contact support.</div>
						</div>
					) : (
						<div style={{ display: "flex", justifyContent: "center" }}>
							<div
								className="cf-turnstile"
								data-sitekey={turnstileSiteKey}
								data-theme="light"
								data-size="normal"
								data-action="login"
								data-before-interactive-callback="onTurnstileVerifying"
								data-callback="onTurnstileSuccess"
								data-expired-callback="onTurnstileExpired"
								data-error-callback="onTurnstileError"
							></div>
						</div>
					)}

					{errorMessage ? <p style={{ margin: 0, color: "#b42318", fontSize: "14px" }}>{errorMessage}</p> : null}

					<button
						type="submit"
						disabled={isSubmitting || !isTurnstileConfigured || isCaptchaVerifying || !captchaToken}
						style={{
							width: "100%",
							height: "48px",
							marginTop: "4px",
							border: 0,
							borderRadius: "5px",
							backgroundColor: "#0096ff",
							color: "#ffffff",
							fontSize: "16px",
							fontWeight: 700,
							letterSpacing: "0.04em",
							textTransform: "uppercase",
							cursor: isSubmitting ? "default" : "pointer",
							opacity: isSubmitting ? 0.85 : 1,
						}}
					>
						{isSubmitting ? "Logging in..." : !isTurnstileConfigured ? "unavailable" : "Log in"}
					</button>
				</form>
			)}
		</div>
	);
};

export default Login;
